import { Pipe, PipeTransform } from '@angular/core';

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 60 * SECONDS_PER_MINUTE;
const SECONDS_PER_DAY = 24 * SECONDS_PER_HOUR;

const MONTH_ABBREVIATIONS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * Formats a past timestamp as a short relative-time string per the design
 * system Timestamps guideline (shortened-display column):
 *
 *   < 1m     → `now`
 *   < 60m    → `Nm ago`
 *   < 24h    → `Nh ago`
 *   1d       → `1d ago`
 *   2-6d     → `Nd ago`
 *   7d       → `1w ago`
 *   > 7d     → `MMM DD` (or `MMM DD, YYYY` if not current year)
 *
 * Pure pipe: re-evaluates only when the input reference changes.
 *
 * @see https://www.notion.so/Timestamps-2149436c37f880b0b5a2ed5e9d0635cb
 */
@Pipe({
  name: 'cxpRelativeTime',
  standalone: true,
})
export class CxpRelativeTimePipe implements PipeTransform {
  transform(value: string | Date | null | undefined, now: Date = new Date()): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return '';

    const diffSeconds = Math.max(0, Math.floor((now.getTime() - date.getTime()) / 1000));

    if (diffSeconds < SECONDS_PER_MINUTE) return 'now';

    const minutes = Math.floor(diffSeconds / SECONDS_PER_MINUTE);
    if (minutes < 60) return `${minutes}m ago`;

    const hours = Math.floor(diffSeconds / SECONDS_PER_HOUR);
    if (hours < 24) return `${hours}h ago`;

    const days = Math.floor(diffSeconds / SECONDS_PER_DAY);
    if (days === 1) return '1d ago';
    if (days < 7) return `${days}d ago`;
    if (days === 7) return '1w ago';

    return formatAbsoluteShortDate(date, now);
  }
}

function formatAbsoluteShortDate(date: Date, now: Date): string {
  const month = MONTH_ABBREVIATIONS[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  if (year === now.getFullYear()) {
    return `${month} ${day}`;
  }
  return `${month} ${day}, ${year}`;
}
